import * as React from 'react';

import {
  endOfToday,
  endOfYesterday,
  isSameDay,
  startOfToday,
  startOfYesterday,
  subDays,
  endOfDay,
  endOfMonth,
  startOfISOWeek,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { DateRange } from 'src/utils/date';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { DateRangePicker, StaticRange, Range } from 'react-date-range';
import { styled, useTheme } from '@mui/material';

interface Props {
  disabled?: boolean;
  months?: number;
  open: boolean;
  selectedRange: DateRange;
  onChange: (d: DateRange) => void;
}

const getYesterdayRange: () => DateRange = () => ({
  startDate: startOfYesterday(),
  endDate: endOfYesterday(),
});
const getTodayRange: () => DateRange = () => ({
  startDate: startOfToday(),
  endDate: endOfToday(),
});

const getLast7DaysRange = (): DateRange => {
  return {
    startDate: subDays(new Date(), 7),
    endDate: endOfToday(),
  };
};

const getThisWeekRange: () => DateRange = () => ({
  startDate: startOfISOWeek(new Date()),
  endDate: endOfToday(),
});

const getThisMonthRange: () => DateRange = () => ({
  startDate: startOfMonth(new Date()),
  endDate: endOfToday(),
});

const getLastMonthRange = (): DateRange => ({
  startDate: startOfMonth(subMonths(new Date(), 1)),
  endDate: endOfMonth(subMonths(new Date(), 1)),
});

const hasSameRangeDay =
  (range1: DateRange) =>
  (range2: Range): boolean => {
    const sameStartDay = range2.startDate ? isSameDay(range1.startDate, range2.startDate) : false;
    const sameEndDay = range2.endDate ? isSameDay(range1.endDate, range2.endDate) : false;

    return sameEndDay && sameStartDay;
  };

export const defaultRange = {
  label: 'Last 7 Days',
  isSelected: hasSameRangeDay(getLast7DaysRange()),
  range: getLast7DaysRange,
};

export const getPredefinedDates = (): (Omit<StaticRange, 'label' | 'range'> & {
  label: string;
  range: () => { startDate: Date; endDate: Date };
})[] => [
  {
    label: 'Today',
    isSelected: hasSameRangeDay(getTodayRange()),
    range: getTodayRange,
  },
  {
    label: 'Yesterday',
    isSelected: hasSameRangeDay(getYesterdayRange()),
    range: getYesterdayRange,
  },
  {
    label: 'This Week',
    isSelected: hasSameRangeDay(getThisWeekRange()),
    range: getThisWeekRange,
  },
  defaultRange,
  {
    label: 'This Month',
    isSelected: hasSameRangeDay(getThisMonthRange()),
    range: getThisMonthRange,
  },
  {
    label: 'Last Month',
    isSelected: hasSameRangeDay(getLastMonthRange()),
    range: getLastMonthRange,
  },
];

const CustomDateRangePicker = styled(DateRangePicker)(({ theme }) => ({
  '& .rdrDefinedRangesWrapper': {
    fontSize: 'inherit',
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
    '& select': {
      '&::-webkit-scrollbar': {
        width: 8,
      },
      '&::-webkit-scrollbar-track': {
        background: theme.palette.background.default,
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[300],
        width: 6,
        borderRadius: 6,
      },
    },
    '& option': {
      backgroundColor: theme.palette.background.default,
    },
  },
}));

const DatePicker: React.FC<Props> = (props) => {
  const rangeKey = 'rangeKey';
  const theme = useTheme();

  return props.open ? (
    <CustomDateRangePicker
      ranges={[{ key: rangeKey, ...props.selectedRange }]}
      rangeColors={[theme.palette.primary.main]}
      preventSnapRefocus={true}
      staticRanges={getPredefinedDates()}
      editableDateInputs={true}
      moveRangeOnFirstSelection={false}
      direction="horizontal"
      months={props.months ?? 2}
      inputRanges={[]}
      onChange={(d) => {
        const newDate = d[rangeKey];
        if (!newDate) return;
        const predefinedDate = getPredefinedDates().find((r) => hasSameRangeDay(r.range())(newDate));

        const dateRange = predefinedDate
          ? {
              startDate: predefinedDate.range().startDate,
              endDate: predefinedDate.range().endDate,
              label: predefinedDate.label,
            }
          : {
              startDate: newDate.startDate ?? new Date(),
              endDate: endOfDay(newDate.endDate ?? new Date()),
            };

        props.onChange(dateRange);
      }}
    />
  ) : null;
};

export default DatePicker;
